import { useState, useEffect } from "react";
import axios from "axios";
import { 
  ArrowLeft, Phone, Mail, Calendar, Clock, User, FileText,
  Copy, Check, Trash2, Download, MessageSquare, Activity,
  Loader2, Edit2, Save, X, AlertTriangle, Mic, Play, Pause
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  waitlist: "bg-amber-100 text-amber-700",
  contacted: "bg-violet-100 text-violet-700",
  qualified: "bg-emerald-100 text-emerald-700",
  unsuitable: "bg-red-100 text-red-700",
  contract_sent: "bg-orange-100 text-orange-700",
  hired: "bg-green-100 text-green-700",
  appointment_scheduled: "bg-cyan-100 text-cyan-700",
  interview_done: "bg-teal-100 text-teal-700",
  rejected: "bg-red-100 text-red-700"
};

const STATUS_LABELS = {
  new: "Neu",
  waitlist: "Nicht erreicht",
  contacted: "Kann erst später",
  qualified: "Qualifiziert",
  unsuitable: "Ungeeignet",
  contract_sent: "Weitergeleitet an T2M",
  hired: "Eingestellt",
  appointment_scheduled: "Termin vereinbart",
  interview_done: "Interview erledigt",
  rejected: "Abgelehnt"
};

const EVENT_LABELS = {
  lead_created: "Lead erstellt",
  lead_updated: "Lead aktualisiert",
  status_changed: "Status geändert",
  exported: "Exportiert",
  deleted: "Gelöscht",
  note_added: "Notiz hinzugefügt",
  lead_claimed: "Lead übernommen",
  lead_released: "Lead freigegeben",
  notes_updated: "Notizen aktualisiert"
};

export const AdminLeadDetail = ({ leadId, authToken, onBack, onViewLead }) => {
  const [lead, setLead] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [events, setEvents] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [voiceMessage, setVoiceMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const voiceAudioRef = useState(null);
  const [duplicateLeads, setDuplicateLeads] = useState([]);
  const [smsPopup, setSmsPopup] = useState(null); // {text, type} for SMS popup
  const [smsCopied, setSmsCopied] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    fetchLeadDetail();
    fetchEmailHistory();
    fetchDuplicateLeads();
  }, [leadId]);

  const fetchLeadDetail = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/leads/${leadId}`, getAuthHeaders());
      setLead(response.data.lead);
      setTranscript(response.data.transcript);
      setEvents(response.data.events || []);
      setNotes(response.data.lead.notes || "");
      setVoiceMessage(response.data.voice_message || null);
    } catch (error) {
      console.error("Error fetching lead:", error);
      toast.error("Fehler beim Laden des Leads");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDuplicateLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads/${leadId}/duplicates`, getAuthHeaders());
      setDuplicateLeads(response.data.duplicates || []);
    } catch (error) {
      console.log("No duplicates found or endpoint not available");
      setDuplicateLeads([]);
    }
  };

  const fetchEmailHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads/${leadId}/email-history`, getAuthHeaders());
      setEmailHistory(response.data.email_history || []);
    } catch (error) {
      console.error("Error fetching email history:", error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setIsSaving(true);
    try {
      const response = await axios.put(`${API}/admin/leads/${leadId}`, { status: newStatus }, getAuthHeaders());
      setLead(prev => ({ ...prev, status: newStatus }));
      
      // Check if auto-email was sent (status -> qualified)
      if (response.data.auto_email_sent) {
        toast.success("Status aktualisiert + E-Mail automatisch versendet!");
      } else {
        toast.success("Status aktualisiert");
      }
      
      // Show SMS popup if SMS text is available
      if (response.data.sms_text) {
        setSmsPopup({ text: response.data.sms_text, type: "qualified" });
        setSmsCopied(false);
      }
      
      fetchLeadDetail(); // Refresh events
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/leads/${leadId}`, { notes }, getAuthHeaders());
      setLead(prev => ({ ...prev, notes }));
      setIsEditingNotes(false);
      toast.success("Notizen gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, getAuthHeaders());
      toast.success("Lead gelöscht");
      onBack();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleExport = async (format = "json") => {
    try {
      if (format === "csv") {
        const response = await axios.get(`${API}/admin/leads/${leadId}/export?format=csv`, {
          ...getAuthHeaders(),
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `lead_${leadId}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const response = await axios.get(`${API}/admin/leads/${leadId}/export`, getAuthHeaders());
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `lead_${leadId}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success("Export erfolgreich");
    } catch (error) {
      toast.error("Fehler beim Export");
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const copyTranscriptToClipboard = () => {
    if (!transcript || !transcript.transcript_json) return;
    
    // Format transcript as readable text
    const header = `Chat-Verlauf für ${lead?.first_name || 'Bewerber'}\n`;
    const separator = "─".repeat(40) + "\n";
    
    const formattedMessages = transcript.transcript_json.map(msg => {
      const sender = msg.role === 'user' ? '👤 Bewerber' : '🤖 Bot';
      const timestamp = msg.timestamp ? ` (${formatDate(msg.timestamp)})` : '';
      return `${sender}${timestamp}:\n${msg.content}`;
    }).join('\n\n');
    
    const fullText = header + separator + formattedMessages;
    
    navigator.clipboard.writeText(fullText);
    setCopiedField('transcript');
    toast.success("Chat-Verlauf kopiert!");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Lead nicht gefunden</p>
        <Button onClick={onBack} variant="outline" className="mt-4 rounded-lg">
          Zurück zur Liste
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="rounded-lg">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{lead.first_name}</h1>
            <p className="text-slate-500 text-sm">Lead #{lead.id.slice(0, 8)}</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-lg" onClick={() => handleExport("json")}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            variant="outline" 
            className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Löschen
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Lead Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Applicant Details */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-slate-400" />
                Bewerber-Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Name</p>
                <p className="font-medium text-slate-900">{lead.first_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Alter</p>
                <p className="font-medium text-slate-900">{lead.age} Jahre</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Wohnort</p>
                <p className="font-medium text-slate-900">{lead.wohnort || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Startklar ab</p>
                <p className="font-medium text-slate-900">{lead.start_date}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Quelle</p>
                <p className="font-medium text-slate-900 capitalize">{lead.source}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Telefon</p>
                  <span
                    onClick={() => {
                      if (window.callRelayTrigger) {
                        window.callRelayTrigger(lead.phone, lead.first_name, lead.id);
                      } else {
                        window.location.href = `tel:${lead.phone}`;
                      }
                    }}
                    className="font-medium text-emerald-600 hover:text-emerald-700 hover:underline cursor-pointer"
                    title="Anrufen via CallRelay"
                    data-testid="lead-phone-call"
                  >
                    {lead.phone}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-lg h-8 w-8 text-blue-500 border-blue-200 hover:bg-blue-50"
                  onClick={() => {
                    const msg = prompt('SMS-Text eingeben:');
                    if (msg && window.callRelaySms) {
                      window.callRelaySms(lead.phone, msg, lead.first_name, lead.id);
                    }
                  }}
                  title="SMS senden"
                  data-testid="lead-phone-sms"
                >
                  <MessageSquare className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-lg h-8 w-8"
                  onClick={() => copyToClipboard(lead.phone, 'phone')}
                >
                  {copiedField === 'phone' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">E-Mail</p>
                  <a href={`mailto:${lead.email}`} className="font-medium text-blue-600 hover:underline break-all">
                    {lead.email}
                  </a>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-lg h-8 w-8"
                  onClick={() => copyToClipboard(lead.email, 'email')}
                >
                  {copiedField === 'email' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              {lead.message && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Nachricht</p>
                  <p className="text-slate-700">{lead.message}</p>
                </div>
              )}
              {lead.duplicate_of && (
                <div className="sm:col-span-2 p-3 bg-pink-50 border border-pink-200 rounded-lg">
                  <p className="text-xs text-pink-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Duplikat
                  </p>
                  <p className="text-pink-700 text-sm">
                    Dieser Lead existierte bereits (gleiche Telefon/E-Mail). Original-Lead:{' '}
                    <button
                      onClick={() => onViewLead && onViewLead(lead.duplicate_of)}
                      className="font-semibold underline hover:text-pink-900 cursor-pointer"
                      data-testid="duplicate-original-link"
                    >
                      #{lead.duplicate_of.slice(0, 8)}...
                    </button>
                  </p>
                </div>
              )}
              {duplicateLeads.length > 0 && (
                <div className="sm:col-span-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Copy className="w-3 h-3" />
                    {duplicateLeads.length} Duplikat{duplicateLeads.length > 1 ? 'e' : ''} gefunden
                  </p>
                  <div className="space-y-1.5">
                    {duplicateLeads.map((dup) => (
                      <button
                        key={dup.id}
                        onClick={() => onViewLead && onViewLead(dup.id)}
                        className="w-full text-left flex items-center justify-between p-2 bg-white rounded border border-amber-100 hover:border-amber-300 hover:bg-amber-50 transition-colors cursor-pointer"
                        data-testid={`duplicate-lead-${dup.id}`}
                      >
                        <span className="text-sm font-medium text-slate-700">
                          {dup.first_name || 'Unbekannt'}
                        </span>
                        <span className="text-xs text-slate-500">
                          {dup.created_at ? new Date(dup.created_at).toLocaleDateString('de-DE') : ''} · #{dup.id.slice(0, 8)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Berater-Zuweisung */}
              {lead.assigned_to && (
                <div className="sm:col-span-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <User className="w-3 h-3" />
                    Zugewiesener Berater
                  </p>
                  <p className="text-emerald-700 font-medium">
                    {lead.assigned_to_name || "Unbekannt"}
                  </p>
                  {lead.assigned_at && (
                    <p className="text-emerald-600 text-xs mt-1">
                      Übernommen am: {formatDate(lead.assigned_at)}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Qualification */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-400" />
                Qualifizierung
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-4">
              {/* Onboarding-Daten aus Button-Flow */}
              {lead.is_18_plus !== undefined && lead.is_18_plus !== null && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">18+:</span>
                  <Badge variant={lead.is_18_plus ? "default" : "destructive"}>
                    {lead.is_18_plus ? "Ja" : "Nein"}
                  </Badge>
                </div>
              )}
              {lead.age_group && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Altersgruppe:</span>
                  <Badge variant="outline">{lead.age_group}</Badge>
                </div>
              )}
              {lead.current_work_status && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Arbeitsstatus:</span>
                  <Badge variant="outline">{lead.current_work_status}</Badge>
                </div>
              )}
              {lead.available_from && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Verfügbar ab:</span>
                  <Badge variant="outline">{lead.available_from}</Badge>
                </div>
              )}
              {lead.job_preference && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Interesse:</span>
                  <Badge variant="outline">{lead.job_preference}</Badge>
                </div>
              )}
              {/* Bestehende Qualifizierungsdaten */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Reisebereit:</span>
                <Badge variant={lead.travel_ready ? "default" : "secondary"}>
                  {lead.travel_ready ? "Ja" : "Nein"}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Vollzeit:</span>
                <Badge variant={lead.full_time ? "default" : "secondary"}>
                  {lead.full_time ? "Ja" : "Nein"}
                </Badge>
              </div>
              {/* FactoryHype-spezifische Qualifizierungsfelder */}
              {lead.job_type && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Job-Typ:</span>
                  <Badge variant="outline">{lead.job_type}</Badge>
                </div>
              )}
              {lead.promo_experience && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Promo-Erfahrung:</span>
                  <Badge variant="outline">{lead.promo_experience}</Badge>
                </div>
              )}
              {lead.tour_model_ok && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">3-4 Wochen Tour:</span>
                  <Badge variant={lead.tour_model_ok.includes("Ja") ? "default" : "secondary"}>
                    {lead.tour_model_ok}
                  </Badge>
                </div>
              )}
              {lead.zoom_call_ok && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Zoom-Call:</span>
                  <Badge variant="default">{lead.zoom_call_ok}</Badge>
                </div>
              )}
              {lead.city && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Stadt:</span>
                  <Badge variant="outline">{lead.city}</Badge>
                </div>
              )}
              {lead.available_days && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Verfügbare Tage:</span>
                  <Badge variant="outline">{lead.available_days}</Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Chat Transcript */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-slate-400" />
                Chat-Verlauf
              </CardTitle>
              {transcript && transcript.transcript_json?.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-lg"
                  onClick={() => copyTranscriptToClipboard()}
                  data-testid="copy-transcript-btn"
                >
                  {copiedField === 'transcript' ? (
                    <>
                      <Check className="w-4 h-4 mr-2 text-green-500" />
                      Kopiert!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Chat kopieren
                    </>
                  )}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {transcript && transcript.transcript_json?.length > 0 ? (
                <ScrollArea className="h-64 pr-4">
                  <div className="space-y-3">
                    {transcript.transcript_json.map((msg, idx) => (
                      <div 
                        key={idx} 
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                            msg.role === 'user' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                          {msg.timestamp && (
                            <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-blue-200' : 'text-slate-400'}`}>
                              {formatDate(msg.timestamp)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <p className="text-slate-500 text-center py-8">Kein Chat-Verlauf verfügbar</p>
              )}
            </CardContent>
          </Card>

          {/* Voice Message */}
          {voiceMessage && voiceMessage.has_voice && (
            <Card className="bg-white border-slate-200 border-purple-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mic className="w-5 h-5 text-purple-500" />
                  Sprachnachricht
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Audio Player */}
                <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                  <audio 
                    id="voice-audio-player"
                    src={`${API}/voice/audio/${voiceMessage.audio_path}`}
                    onEnded={() => setIsPlayingVoice(false)}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-12 h-12 rounded-full border-purple-300 hover:bg-purple-100"
                    onClick={() => {
                      const audio = document.getElementById('voice-audio-player');
                      if (isPlayingVoice) {
                        audio.pause();
                        setIsPlayingVoice(false);
                      } else {
                        audio.play();
                        setIsPlayingVoice(true);
                      }
                    }}
                    data-testid="play-voice-btn"
                  >
                    {isPlayingVoice ? (
                      <Pause className="w-6 h-6 text-purple-600" />
                    ) : (
                      <Play className="w-6 h-6 text-purple-600 ml-1" />
                    )}
                  </Button>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">Audio-Aufnahme</p>
                    <p className="text-xs text-slate-500">
                      {voiceMessage.created_at ? new Date(voiceMessage.created_at).toLocaleString('de-DE') : 'Unbekannt'}
                    </p>
                  </div>
                </div>
                
                {/* Transcript */}
                {voiceMessage.transcript && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-slate-500 mb-2">Transkript:</p>
                    <p className="text-sm text-slate-700 italic">"{voiceMessage.transcript}"</p>
                  </div>
                )}
                
                {!voiceMessage.transcript && (
                  <p className="text-sm text-amber-600 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Transkript nicht verfügbar
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Resume/CV Files */}
          {lead.resume_files && lead.resume_files.length > 0 && (
            <Card className="bg-white border-slate-200 border-amber-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-500" />
                  Bewerbungsunterlagen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {lead.resume_files.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-3">
                      {file.content_type?.startsWith('image/') ? (
                        <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-amber-600" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded bg-red-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-red-600" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                          {file.filename}
                        </p>
                        <p className="text-xs text-slate-500">
                          {file.uploaded_at ? new Date(file.uploaded_at).toLocaleString('de-DE') : ''}
                          {file.size ? ` • ${(file.size / 1024).toFixed(0)} KB` : ''}
                        </p>
                      </div>
                    </div>
                    <a
                      href={`${API}/resume/file/${file.storage_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors"
                      data-testid={`download-resume-${idx}`}
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-slate-400" />
                Interne Notizen
              </CardTitle>
              {!isEditingNotes ? (
                <Button variant="ghost" size="sm" onClick={() => setIsEditingNotes(true)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setIsEditingNotes(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={handleSaveNotes} disabled={isSaving}>
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {isEditingNotes ? (
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notizen zum Bewerber..."
                  rows={4}
                  className="resize-none"
                />
              ) : (
                <p className={`whitespace-pre-wrap ${notes ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                  {notes || "Keine Notizen vorhanden"}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Status & Activity */}
        <div className="space-y-6">
          {/* Status Control */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Aktueller Status:</span>
                <Badge className={`${STATUS_COLORS[lead.status]} border-0`}>
                  {STATUS_LABELS[lead.status]}
                </Badge>
              </div>
              
              <Select value={lead.status} onValueChange={handleStatusChange} disabled={isSaving}>
                <SelectTrigger className="w-full rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button 
                className="w-full rounded-lg bg-gradient-to-r from-blue-600 to-violet-600"
                onClick={() => handleStatusChange('contacted')}
                disabled={lead.status === 'contacted' || isSaving}
              >
                <Phone className="w-4 h-4 mr-2" />
                Als kontaktiert markieren
              </Button>
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-400" />
                Zeitstempel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Erstellt</p>
                <p className="text-sm font-medium text-slate-900">{formatDate(lead.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Zuletzt aktualisiert</p>
                <p className="text-sm font-medium text-slate-900">{formatDate(lead.updated_at)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Activity Log */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-slate-400" />
                Aktivität
              </CardTitle>
            </CardHeader>
            <CardContent>
              {events.length > 0 ? (
                <ScrollArea className="h-48">
                  <div className="space-y-3">
                    {events.map((event) => (
                      <div key={event.id} className="flex gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-slate-300 mt-2 flex-shrink-0" />
                        <div>
                          <p className="text-slate-700">
                            {EVENT_LABELS[event.event_type] || event.event_type}
                            {event.event_data?.new_status && (
                              <span className="text-slate-500"> → {STATUS_LABELS[event.event_data.new_status]}</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400">
                            {formatDate(event.created_at)} • {event.actor}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <p className="text-slate-500 text-center py-4">Keine Aktivitäten</p>
              )}
            </CardContent>
          </Card>

          {/* Email History */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                E-Mail Verlauf
                {emailHistory.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{emailHistory.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {emailHistory.length > 0 ? (
                <ScrollArea className="h-48">
                  <div className="space-y-3">
                    {emailHistory.map((email, idx) => (
                      <div key={idx} className="flex gap-3 text-sm border-b border-slate-100 pb-3 last:border-0">
                        <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-slate-700 font-medium">{email.subject}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(email.sent_at).toLocaleString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                            <span> • Absender: chat@viralehooks.de</span>
                            {email.sent_by && <span> • gesendet durch: {email.sent_by}</span>}
                            {email.template && <span> • Vorlage: {email.template}</span>}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <p className="text-slate-500 text-center py-4">Noch keine E-Mails gesendet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lead löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Lead "{lead.first_name}" wird dauerhaft gelöscht inkl. Chat-Verlauf und Aktivitäten. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-lg">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SMS Popup - shown after auto-email is sent */}
      <AlertDialog open={!!smsPopup} onOpenChange={(open) => !open && setSmsPopup(null)}>
        <AlertDialogContent className="max-w-md rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-emerald-700">
              <MessageSquare className="w-5 h-5" />
              {smsPopup?.type === "reminder" ? "Erinnerungs-SMS versenden" : "SMS jetzt über CallRelay senden"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm text-slate-500">
                  {smsPopup?.type === "reminder" 
                    ? "Die Erinnerungs-E-Mail wurde versendet. Bitte sende jetzt die SMS über CallRelay:"
                    : "Die E-Mail wurde automatisch versendet. Bitte sende jetzt die SMS über CallRelay:"
                  }
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">
                  {smsPopup?.text}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="rounded-lg"
              onClick={() => {
                if (smsPopup?.text) {
                  navigator.clipboard.writeText(smsPopup.text);
                  setSmsCopied(true);
                  toast.success("SMS-Text kopiert!");
                  setTimeout(() => setSmsCopied(false), 2000);
                }
              }}
              data-testid="sms-copy-btn"
            >
              {smsCopied ? <Check className="w-4 h-4 mr-1 text-green-500" /> : <Copy className="w-4 h-4 mr-1" />}
              {smsCopied ? "Kopiert!" : "Text kopieren"}
            </Button>
            {lead?.phone && (
              <Button
                className="rounded-lg bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  if (window.callRelaySms && smsPopup?.text) {
                    window.callRelaySms(lead.phone, smsPopup.text, lead.first_name, lead.id);
                    toast.success("SMS wird über CallRelay gesendet!");
                    setSmsPopup(null);
                  } else {
                    navigator.clipboard.writeText(smsPopup?.text || "");
                    toast.info("Text kopiert – bitte manuell in CallRelay einfügen");
                  }
                }}
                data-testid="sms-send-callrelay-btn"
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Via CallRelay senden
              </Button>
            )}
            <AlertDialogCancel className="rounded-lg" onClick={() => setSmsPopup(null)}>
              Schließen
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminLeadDetail;
